import React, { useEffect, useState } from 'react';
import Modal from '../components/Modal';
import { planService } from '../services';
import parseCsv from '../utils/parseCsv';
import './plan-overlay.scss';

const HIDDEN_COLUMNS = ['General Note'];

const isBlankRow = row => row.every(cell => cell.trim() === '');

// The plan CSV is a title row followed by sections separated by blank rows.
// A section may start with a single-cell title row, then a header row and data rows.
const toSections = rows => {
    const sections = [];
    let current = [];

    rows.slice(1).forEach(row => {
        if (isBlankRow(row)) {
            if (current.length) sections.push(current);
            current = [];
        } else {
            current.push(row);
        }
    });
    if (current.length) sections.push(current);

    return sections.map((sectionRows, index) => {
        const hasTitle = sectionRows[0].length === 1;
        const title = hasTitle ? sectionRows[0][0] : index === 0 ? 'Plan' : '';
        const [header = [], ...body] = hasTitle ? sectionRows.slice(1) : sectionRows;
        const visibleColumns = header
            .map((name, columnIndex) => ({ name: name.trim(), columnIndex }))
            .filter(({ name }) => !HIDDEN_COLUMNS.includes(name));

        return { title, visibleColumns, body };
    });
};

const DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;

// Sort value for a cell: dates (M/D/YYYY) and numbers (incl. percentages) sort by value,
// everything else as text. Empty cells and "No data" sort last.
const toSortValue = cell => {
    const value = (cell || '').trim();
    if (value === '' || value.toLowerCase() === 'no data') return null;

    const date = value.match(DATE_PATTERN);
    if (date) {
        const year = date[3].length === 2 ? 2000 + Number(date[3]) : Number(date[3]);
        return new Date(year, date[1] - 1, date[2]).getTime();
    }

    const number = Number(value.replace(/%$/, ''));
    return Number.isNaN(number) ? value.toLowerCase() : number;
};

const compareCells = (a, b) => {
    const valueA = toSortValue(a);
    const valueB = toSortValue(b);
    if (valueA === null || valueB === null) {
        return valueA === valueB ? 0 : valueA === null ? 1 : -1;
    }
    if (typeof valueA === 'number' && typeof valueB === 'number') return valueA - valueB;
    return String(valueA).localeCompare(String(valueB), undefined, { numeric: true });
};

const SortableTable = ({ columns, rows }) => {
    const [sort, setSort] = useState({ columnIndex: null, direction: 'asc' });

    const onSort = columnIndex => {
        setSort(current => ({
            columnIndex,
            direction:
                current.columnIndex === columnIndex && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedRows =
        sort.columnIndex === null
            ? rows
            : [...rows].sort((a, b) => {
                  const result = compareCells(a[sort.columnIndex], b[sort.columnIndex]);
                  // Keep empty cells last in both directions
                  const hasEmpty =
                      toSortValue(a[sort.columnIndex]) === null ||
                      toSortValue(b[sort.columnIndex]) === null;
                  return sort.direction === 'asc' || hasEmpty ? result : -result;
              });

    return (
        <table className="plan-overlay__table">
            <thead>
                <tr>
                    {columns.map(({ name, columnIndex }) => {
                        const isSorted = sort.columnIndex === columnIndex;
                        return (
                            <th
                                key={name}
                                aria-sort={
                                    isSorted
                                        ? sort.direction === 'asc'
                                            ? 'ascending'
                                            : 'descending'
                                        : 'none'
                                }
                            >
                                <button
                                    className="plan-overlay__sort"
                                    onClick={() => onSort(columnIndex)}
                                >
                                    {name}
                                    <span className="plan-overlay__sort-icon">
                                        {isSorted ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}
                                    </span>
                                </button>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {sortedRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {columns.map(({ name, columnIndex }) => (
                            <td key={name}>{row[columnIndex]}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const PlanOverlay = ({ plan, onClose }) => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(false);
        planService
            .fetchPlanCsv(plan.id)
            .then(response => {
                setSections(toSections(parseCsv(response.data)));
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [plan.id]);

    return (
        <Modal onClose={onClose} loading={loading} wide>
            <div className="plan-overlay">
                <h2>{plan.name}</h2>
                {error && <p>Unable to load this plan.</p>}
                {sections.map((section, sectionIndex) => (
                    <div className="plan-overlay__section" key={sectionIndex}>
                        {section.title && <h3>{section.title}</h3>}
                        {section.body.length === 0 ? (
                            <p className="plan-overlay__empty">No entries.</p>
                        ) : (
                            <SortableTable columns={section.visibleColumns} rows={section.body} />
                        )}
                    </div>
                ))}
            </div>
        </Modal>
    );
};

export default PlanOverlay;
